// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api, { AUTH_REQUEST_TIMEOUT_MS } from '../services/api';
import { jwtDecode } from 'jwt-decode';
import { clearClientAuthStorage } from '../utils/clearClientAuthStorage';
import { safeLocalStorage } from '../utils/safeLocalStorage';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(null);
  const [user, setUser] = useState(null);
  const [channelId, setChannel] = useState(null);
  const [branchId, setBranch] = useState(null);
  const [commerceName, setCommerceName] = useState(null);
  const [commerceLogo, setCommerceLogo] = useState(null);
  const [branchName, setBranchName] = useState(null);
  const [config, setConfig] = useState(null);
  const [posRole, setPosRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutInProgress = useRef(false);

  const clearSession = () => {
    setAuthToken(null);
    setUser(null);
    setChannel(null);
    setBranch(null);
    setCommerceName(null);
    setCommerceLogo(null);
    setBranchName(null);
    setConfig(null);
    setPosRole(null);
    clearClientAuthStorage();
  };

  useEffect(() => {
    const token = safeLocalStorage.getItem('authToken');
    const storedUser = safeLocalStorage.getItem('user');
    const storedBranch = safeLocalStorage.getItem('branchId');
    const storedChannel = safeLocalStorage.getItem('channelId');
    const storedCommerce = safeLocalStorage.getItem('commerceName');
    const storedLogo = safeLocalStorage.getItem('commerceLogo');
    const storedBranchName = safeLocalStorage.getItem('branchName');
    const storedConfig = safeLocalStorage.getItem('config');
    const storedPosRole = safeLocalStorage.getItem('posRole');

    if (token && token !== 'undefined') {
      try {
        const decodedToken = jwtDecode(token);

        if (!decodedToken?.exp || decodedToken.exp * 1000 < Date.now()) {
          clearSession();
        } else {
          setAuthToken(token);
          setUser(storedUser ? JSON.parse(storedUser) : null);
          setBranch(storedBranch);
          setChannel(storedChannel);
          setCommerceName(storedCommerce);
          setCommerceLogo(storedLogo);
          setBranchName(storedBranchName);
          setConfig(storedConfig ? JSON.parse(storedConfig) : null);
          setPosRole(storedPosRole ? JSON.parse(storedPosRole) : null);
        }
      } catch (error) {
        console.error('Invalid stored auth token:', error);
        clearSession();
      }
    }
    setLoading(false);
  }, []);

  const login = (token, userData, channelId, branchId, commerceName, branchName, commerceLogo, config, role) => {
    setAuthToken(token);
    setUser(userData);
    setChannel(channelId);
    setBranch(branchId);
    setCommerceName(commerceName);
    setCommerceLogo(commerceLogo);
    setBranchName(branchName);
    setConfig(config);
    setPosRole(role ?? null);
    safeLocalStorage.setItem('authToken', token);
    safeLocalStorage.setItem('user', JSON.stringify(userData));
    safeLocalStorage.setItem('branchId', branchId);
    safeLocalStorage.setItem('channelId', channelId);
    safeLocalStorage.setItem('commerceName', commerceName);
    safeLocalStorage.setItem('commerceLogo', commerceLogo);
    safeLocalStorage.setItem('branchName', branchName);
    safeLocalStorage.setItem('config', JSON.stringify(config));
    if (role) {
      safeLocalStorage.setItem('posRole', JSON.stringify(role));
    } else {
      safeLocalStorage.removeItem('posRole');
    }
  };

  const logout = async () => {
    if (logoutInProgress.current) {
      return;
    }

    logoutInProgress.current = true;
    const token = authToken || safeLocalStorage.getItem('authToken');

    try {
      if (token) {
        await api.post('/logout', {}, {
          headers: { Authorization: `Bearer ${token}` },
          skipUnauthorizedRedirect: true,
          timeout: AUTH_REQUEST_TIMEOUT_MS,
          networkRetry: true,
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      clearSession();
      logoutInProgress.current = false;
    }
  };  

  return (
    <AuthContext.Provider
      value={{
        authToken,
        user,
        channelId,
        branchId,
        commerceName,
        branchName,
        commerceLogo,
        config,
        setConfig,
        posRole,
        login,
        logout,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
